import React, { useEffect, useState } from "react"; // Added useState
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Loader2, AlertCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import ImageUploader from "./ImageUploader";
import VideoUploader from "./VideoUploader"; // Import VideoUploader
import {
  ListingFormProvider,
  useListingForm,
} from "@/context/ListingFormContext";
import ListingBasicInfo from "./ListingBasicInfo";
import ListingCategorySection from "./ListingCategorySection";
import ListingLocationSection from "./ListingLocationSection";
import ListingDeliverySection from "./ListingDeliverySection";
import ListingCreditsInfo from "./ListingCreditsInfo";
import ListingPriceSection from "./ListingPriceSection";
import ListingEmailSection from "./ListingEmailSection";
import { useListingSubmit } from "@/hooks/useListingSubmit";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// This interface should align with the output of the get_user_credits RPC function
interface RpcCreditsResult {
  free_listings_remaining: number;
  free_listings_used: number;
  purchased_listings_credits: number;
  total_listings_used: number;
  default_free_listings: number;
  free_stocks_remaining: number; // Assuming RPC provides this
  free_stocks_used: number; // Assuming RPC provides this
  purchased_stocks_credits: number; // Assuming RPC provides this
  default_free_stocks: number; // Assuming RPC provides this
  // max_stock_items might need to be sourced differently if not from this RPC
}

interface CreditsResult {
  free_listings_remaining: number;
  purchased_listings_credits: number;
  max_stock_items: number; // This should come from subscription or platform settings
  default_free_listings: number;
  // other fields if needed by components
}

interface ListingFormProps {
  userId: string;
  editMode?: boolean;
  listingId?: number;
  initialData?: any; // Consider defining a more specific type for initialData
  isAuthenticated?: boolean;
  defaultListingData?: {
    is_active: boolean;
    approval_status: string;
    is_authenticated_listing?: boolean;
    video_url?: string | null; // Add video_url here
    [key: string]: any;
  };
}

const ListingFormContent = ({
  userId,
  editMode = false,
  defaultListingData,
  isAuthenticated = true,
  initialData, // Add initialData here
}: ListingFormProps) => {
  const navigate = useNavigate();
  const {
    formData,
    images,
    stockItems,
    showStockWarning,
    setImages,
    validateForm,
    setShowStockWarning,
  } = useListingForm();

  // Local state for video file
  const [videoFile, setVideoFile] = useState<File | null>(null);
  // Local state for current video URL (for edit mode)
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(
    initialData?.video_url || null
  );
  
  useEffect(() => {
    if (initialData?.video_url) {
      setCurrentVideoUrl(initialData.video_url);
    }
  }, [initialData?.video_url]);

  const { data: userCreditsRpcResult, isLoading: isLoadingCredits } = useQuery({
    queryKey: ["userCreditsRpc", userId],
    queryFn: async (): Promise<CreditsResult | null> => {
      if (!isAuthenticated || !userId || userId === "guest") {
        return {
            free_listings_remaining: 1,
            purchased_listings_credits: 0,
            max_stock_items: 5,
            default_free_listings: 1, 
          } as CreditsResult;
      }
      
      try {
        const { data, error } = await supabase.rpc('get_user_credits', {
          p_user_id: userId,
        });

        if (error) {
          console.error("Error fetching user credits via RPC in ListingForm:", error);
          return {
            free_listings_remaining: 0,
            purchased_listings_credits: 0,
            max_stock_items: 5, 
            default_free_listings: 0,
          } as CreditsResult;
        }
        
        const rpcData = data?.[0] as RpcCreditsResult | undefined;

        if (!rpcData) {
            console.warn("ListingForm: RPC get_user_credits returned no data for user", userId);
             return {
                free_listings_remaining: 0, 
                purchased_listings_credits: 0,
                max_stock_items: 5, 
                default_free_listings: 0,
            } as CreditsResult;
        }
        
        return {
            free_listings_remaining: rpcData.free_listings_remaining ?? 0,
            purchased_listings_credits: rpcData.purchased_listings_credits ?? 0,
            max_stock_items: 5,
            default_free_listings: rpcData.default_free_listings ?? 0,
        } as CreditsResult;

      } catch (error) {
        console.error("Catch block: Error fetching user credits in ListingForm:", error);
        return {
          free_listings_remaining: 0,
          purchased_listings_credits: 0,
          max_stock_items: 5,
          default_free_listings: 0,
        } as CreditsResult;
      }
    },
    enabled: !!userId,
  });
  
  const userCredits = userCreditsRpcResult;

  const freeListingsRemaining = isAuthenticated ? (userCredits?.free_listings_remaining ?? 0) : 1;
  const purchasedListingsCredits = isAuthenticated ? (userCredits?.purchased_listings_credits ?? 0) : 0;
  const totalCredits = freeListingsRemaining + purchasedListingsCredits;
  const stockItemsLimit = isAuthenticated ? (userCredits?.max_stock_items ?? 5) : 5; 
  const hasNoCreditsLeft = isAuthenticated && !editMode && totalCredits <= 0;

  useEffect(() => {
    if (stockItems.length > stockItemsLimit) {
      setShowStockWarning(true);
    } else {
      setShowStockWarning(false);
    }
  }, [stockItems, stockItemsLimit, setShowStockWarning]);

  const { isSubmitting, uploading, uploadError, handleSubmit, videoUploadProgress } =
    useListingSubmit({
      userId,
      validateForm,
      formData,
      images,
      videoFile,
      editMode,
      hasNoCreditsLeft,
      stockItemsOverLimit: showStockWarning,
      stockItemsLimit,
      isAuthenticated,
    });
  
  const handleRemoveCurrentVideo = () => {
    setCurrentVideoUrl(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {editMode ? "Edit Listing" : "Krijo Shpallje të Re"}
        </CardTitle>
        <CardDescription>
          {isAuthenticated 
            ? (editMode
                ? "Update your listing details"
                : `Ju keni ${totalCredits} kredite në dispozicion (${userCredits?.default_free_listings ?? 0} falas mujore)`)
            : "Krijoni një shpallje pa regjistrim (funksionalitet i kufizuar)"
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isAuthenticated && userCredits && (
          <ListingCreditsInfo
            editMode={editMode}
            hasNoCreditsLeft={hasNoCreditsLeft}
            showStockWarning={showStockWarning}
            stockItemsLimit={stockItemsLimit}
            totalCredits={totalCredits}
            freeListingsRemaining={freeListingsRemaining}
            purchasedListingsCredits={purchasedListingsCredits}
          />
        )}

        {!isAuthenticated && (
          <Alert variant="destructive" className="mb-6 bg-amber-50 border-amber-200 text-amber-800">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Funksionalitet i kufizuar</AlertTitle>
            <AlertDescription>
              Si përdorues i paregjistruar, disa funksione nuk do të jenë në dispozicion. 
              <Button
                variant="link" 
                className="pl-1 h-auto p-0 text-amber-600 dark:text-amber-400"
                onClick={() => navigate('/auth')}
              >
                Regjistrohuni për të pasur qasje të plotë
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {uploadError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded text-red-700">
            {uploadError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <ListingBasicInfo />
          <ListingCategorySection />
          <ListingPriceSection />
          <ListingLocationSection />
          <ListingDeliverySection />
          
          {!isAuthenticated && <ListingEmailSection />}

          <div className="mb-4">
            <ImageUploader 
              images={images} 
              setImages={setImages}
              maxImages={5} // Assuming max 5 images
            />
          </div>

          <VideoUploader
            videoFile={videoFile}
            setVideoFile={setVideoFile}
            currentVideoUrl={currentVideoUrl}
            onRemoveCurrentVideo={handleRemoveCurrentVideo}
            maxSizeMB={20}
            isUploading={uploading && !!videoFile} // Show uploading state specifically for video
            uploadProgress={videoUploadProgress}
          />

          <Button
            type="submit"
            disabled={isSubmitting || uploading || (hasNoCreditsLeft && !editMode && isAuthenticated)}
            className="bg-brand-500 hover:bg-brand-600 text-white"
          >
            {isSubmitting || uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Po ngarkohet...
              </>
            ) : (
              editMode ? "Ruaj Ndryshimet" : "Krijo Shpallje"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const ListingForm = (props: ListingFormProps) => {
  const effectiveInitialData = {
    ...(props.initialData || {}),
    ...(props.defaultListingData || {}),
    video_url: props.initialData?.video_url || props.defaultListingData?.video_url || null,
  };

  return (
    <ListingFormProvider
      initialData={effectiveInitialData}
      isAuthenticated={props.isAuthenticated}
    >
      <ListingFormContent {...props} initialData={effectiveInitialData} />
    </ListingFormProvider>
  );
};

export default ListingForm;
